import mapboxgl from "mapbox-gl";
import { MarkerFeature } from "../../../utils/models/MarkerFeature";
import { LineStringFeature } from "../../../utils/models/LineStringFeature";
import { IMapService, MarkerOptions, LineOptions, QueryOptions, MapBounds } from "./IMapService";
import { MapLayerCategory } from "../../../utils/layer-registry";
import { plainToClass } from "class-transformer";

export class MapboxMapService implements IMapService {
  private map: mapboxgl.Map;
  private layerRegistry: any;
  
  constructor(map: mapboxgl.Map, layerRegistry: any) {
    this.map = map;
    this.layerRegistry = layerRegistry;
  }
  
  async addMarker(id: string, marker: MarkerFeature, options: MarkerOptions = {}): Promise<void> {
    const markerFeature = plainToClass(MarkerFeature, marker);
    
    // Remove existing if present
    if (this.hasLayer(id)) {
      this.layerRegistry.removeLayer(id);
    }
    
    // Add source
    this.layerRegistry.addSource(id, markerFeature.toGeoJSONSourceRaw(), true);
    
    // Create layer with special handling for hover marker
    const isHoverMarker = id === 'hover-marker';
    const hoverPaint = {
      "circle-radius": 6,
      "circle-color": "#bbbbbb",
      "circle-opacity": 1,
      "circle-stroke-width": 2,
      "circle-stroke-color": "#ffffff",
      "circle-stroke-opacity": 1,
    };

    const defaultPaint = {
      "circle-radius": options.isActive ? (options.isFirst ? 8 : 6) : (options.isFirst ? 6 : 4),
      "circle-color": options.isActive ? (options.isFirst ? "#4CAF50" : "#fd5372") : (options.isFirst ? "#4CAF50" : "#fd5372"),
      "circle-opacity": options.opacity ?? (options.isActive ? 1.0 : 0.4),
      "circle-stroke-width": options.isActive ? 2 : 1,
      "circle-stroke-color": "#ffffff",
      "circle-stroke-opacity": options.opacity ?? (options.isActive ? 1.0 : 0.4),
    };

    const layer: mapboxgl.CircleLayer = {
      id: id,
      type: "circle",
      source: id,
      paint: isHoverMarker ? hoverPaint : defaultPaint,
    };
    
    const zIndex = options.isActive ? 13 : 11;
    this.layerRegistry.addLayer(layer, MapLayerCategory.Annotations, { z: zIndex });
  }
  
  updateMarkerPosition(id: string, coordinates: [number, number]): void {
    const source = this.map.getSource(id) as mapboxgl.GeoJSONSource;
    if (source && source.type === 'geojson') {
      const currentData = source._data as GeoJSON.Feature;
      if (currentData && currentData.geometry.type === 'Point') {
        currentData.geometry.coordinates = coordinates;
        source.setData(currentData);
      }
    }
  }
  
  removeMarker(id: string): void {
    if (this.hasLayer(id)) {
      this.layerRegistry.removeLayer(id);
    }
  }
  
  async addLineString(id: string, lineString: LineStringFeature, options: LineOptions = {}): Promise<void> {
    const lineFeature = plainToClass(LineStringFeature, lineString);
    
    // Remove existing if present
    if (this.hasLayer(id)) {
      this.layerRegistry.removeLayer(id);
    }
    
    // Add source
    this.layerRegistry.addSource(id, lineFeature.toGeoJsonSource(), true);
    
    // Create layer
    const layer: mapboxgl.LineLayer = {
      id: id,
      type: "line",
      source: id,
      layout: {
        "line-join": "round",
        "line-cap": "round",
      },
      paint: {
        "line-color": options.color ?? "#fd5372",
        "line-width": options.width ?? (options.isActive ? 5 : 3),
        "line-opacity": options.opacity ?? (options.isActive ? 1.0 : 0.4),
      },
    };
    
    const zIndex = options.isActive ? 10 : 8;
    this.layerRegistry.addLayer(layer, MapLayerCategory.Annotations, { z: zIndex });
  }
  
  updateLineString(id: string, lineString: LineStringFeature): void {
    const source = this.map.getSource(id) as mapboxgl.GeoJSONSource;
    if (source && source.type === 'geojson') {
      const lineFeature = plainToClass(LineStringFeature, lineString);
      source.setData(lineFeature.toGeoJson() as any);
    }
  }
  
  removeLineString(id: string): void {
    if (this.hasLayer(id)) {
      this.layerRegistry.removeLayer(id);
    }
  }
  
  queryRenderedFeatures(point: mapboxgl.PointLike, options: QueryOptions = {}): mapboxgl.MapboxGeoJSONFeature[] {
    return this.map.queryRenderedFeatures(point, options);
  }
  
  queryRenderedFeaturesInBox(bbox: [mapboxgl.PointLike, mapboxgl.PointLike], options: QueryOptions = {}): mapboxgl.MapboxGeoJSONFeature[] {
    return this.map.queryRenderedFeatures(bbox, options);
  }
  
  on(event: string, layerId: string, handler: Function): void {
    this.map.on(event as any, layerId, handler as any);
  }
  
  off(event: string, layerId: string, handler: Function): void {
    this.map.off(event as any, layerId, handler as any);
  }
  
  once(event: string, handler: Function): void {
    this.map.once(event as any, handler as any);
  }
  
  getCenter(): [number, number] {
    const center = this.map.getCenter();
    return [center.lng, center.lat];
  }
  
  getZoom(): number {
    return this.map.getZoom();
  }
  
  getBounds(): MapBounds {
    const bounds = this.map.getBounds();
    if (!bounds) {
      // Return default bounds if none available
      return { north: 90, south: -90, east: 180, west: -180 };
    }
    return {
      north: bounds.getNorth(),
      south: bounds.getSouth(),
      east: bounds.getEast(),
      west: bounds.getWest()
    };
  }
  
  fitBounds(bounds: MapBounds, options?: mapboxgl.FitBoundsOptions): void {
    this.map.fitBounds([
      [bounds.west, bounds.south],
      [bounds.east, bounds.north]
    ], options);
  }
  
  project(lngLat: [number, number]): { x: number; y: number } {
    const point = this.map.project(lngLat);
    return { x: point.x, y: point.y };
  }
  
  unproject(point: { x: number; y: number }): [number, number] {
    const lngLat = this.map.unproject(point as any);
    return [lngLat.lng, lngLat.lat];
  }
  
  setPaintProperty(layerId: string, property: string, value: any): void {
    if (this.hasLayer(layerId)) {
      this.map.setPaintProperty(layerId, property as any, value);
    }
  }
  
  setLayoutProperty(layerId: string, property: string, value: any): void {
    if (this.hasLayer(layerId)) {
      this.map.setLayoutProperty(layerId, property as any, value);
    }
  }
  
  setCursor(cursor: string): void {
    this.map.getCanvas().style.cursor = cursor;
  }
  
  hasLayer(layerId: string): boolean {
    return this.map.getLayer(layerId) !== undefined;
  }
  
  hasSource(sourceId: string): boolean {
    return this.map.getSource(sourceId) !== undefined;
  }
}
